import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faPlus, faTrash, faEnvelope, faUserTag, faBuilding, faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';
import { getUserData } from '../../utils/userUtils';

function AllowedUserManagement({ userId, refreshTrigger, onRefresh }) {
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [roles, setRoles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'email',
    direction: 'ascending'
  });

  // Add refs to track if we're currently fetching data
  const isFetchingAllowedUsersRef = useRef(false);
  const isFetchingRolesRef = useRef(false);
  const isFetchingAccountsRef = useRef(false);

  // Check if the current user is a Super Admin
  const checkUserRole = useCallback(async () => {
    try {
      const userData = getUserData();
      setIsSuperAdmin(userData.role === 'Super Admin');
    } catch (error) {
      console.error('Error checking user role:', error);
    }
  }, []);

  // Memoize the fetchAllowedUsers function to prevent unnecessary re-creation
  const fetchAllowedUsers = useCallback(async () => {
    // If we're already fetching allowed users, don't start another fetch
    if (isFetchingAllowedUsersRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingAllowedUsersRef.current = true;
      setIsLoading(true);

      const response = await api.get(getApiUrl('/api/admin/allowed-users'), {
        params: { userId }
      });
      setAllowedUsers(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching allowed users:', error);
      setError('Failed to load allowed users. Please try again later.');
      setIsLoading(false);
    } finally {
      // Reset the fetching flag
      isFetchingAllowedUsersRef.current = false;
    }
  }, [userId]);

  // Memoize the fetchRoles function to prevent unnecessary re-creation
  const fetchRoles = useCallback(async () => {
    // If we're already fetching roles, don't start another fetch
    if (isFetchingRolesRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingRolesRef.current = true;

      const response = await api.get(getApiUrl('/api/admin/roles'), {
        params: { userId }
      });
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      // Reset the fetching flag
      isFetchingRolesRef.current = false;
    }
  }, [userId]);

  // Memoize the fetchAccounts function to prevent unnecessary re-creation
  const fetchAccounts = useCallback(async () => {
    // If we're already fetching accounts, don't start another fetch
    if (isFetchingAccountsRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingAccountsRef.current = true;

      const response = await api.get(getApiUrl('/api/admin/accounts'), {
        params: { userId }
      });
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      // Reset the fetching flag
      isFetchingAccountsRef.current = false;
    }
  }, [userId]);

  // Fetch data when the component mounts or refreshTrigger changes
  useEffect(() => {
    // Use a small timeout to debounce multiple rapid calls
    const debounceTimeout = setTimeout(() => {
      fetchAllowedUsers();
      fetchRoles();
      fetchAccounts();
      checkUserRole();
    }, 50);

    // Clean up the timeout on unmount or when dependencies change
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [fetchAllowedUsers, fetchRoles, fetchAccounts, checkUserRole, refreshTrigger]);

  const handleAddUser = async (e) => {
    e.preventDefault();

    if (!newEmail.trim()) {
      return;
    }

    try {
      const response = await api.post(getApiUrl('/api/admin/allowed-users'), {
        email: newEmail.trim(),
        roleId: selectedRole || undefined,
        accountId: selectedAccount || undefined,
        userId
      });

      // Update local state
      setAllowedUsers([...allowedUsers, response.data]);
      resetForm();

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error adding allowed user:', error);
      setError('Failed to add user: ' + (error.response?.data?.error || error.message));
    }
  };

  const resetForm = () => {
    setNewEmail('');
    setSelectedRole('');
    setSelectedAccount('');
    setShowModal(false);
  };

  const handleDeleteUser = async (allowedUserId) => {
    if (!window.confirm('Are you sure you want to remove this user from the allowed list? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(getApiUrl(`/api/admin/allowed-users/${allowedUserId}`), {
        data: { userId } // Pass userId in the request body for the soft delete operation
      });

      // Update local state
      setAllowedUsers(allowedUsers.filter(user => user.id !== allowedUserId));

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting allowed user:', error);
      setError('Failed to remove user: ' + (error.response?.data?.error || error.message));
    }
  };

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Get the sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      // Handle null or undefined values
      if (a[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? 1 : -1;
      if (b[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? -1 : 1;

      // Handle date fields
      if (sortConfig.key === 'created_at' || sortConfig.key === 'updated_at') {
        const dateA = a[sortConfig.key] ? new Date(a[sortConfig.key]) : new Date(0);
        const dateB = b[sortConfig.key] ? new Date(b[sortConfig.key]) : new Date(0);
        return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA;
      }

      // Handle string fields
      if (typeof a[sortConfig.key] === 'string' && typeof b[sortConfig.key] === 'string') {
        return sortConfig.direction === 'ascending'
          ? a[sortConfig.key].localeCompare(b[sortConfig.key])
          : b[sortConfig.key].localeCompare(a[sortConfig.key]);
      }

      // Handle other fields
      return sortConfig.direction === 'ascending'
        ? a[sortConfig.key] > b[sortConfig.key] ? 1 : -1
        : a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
    });
  };

  // Get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  // Apply sorting to allowed users
  const sortedAllowedUsers = getSortedData(allowedUsers);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          {/* <div className="loading-spinner"></div> */}
          <p>Loading allowed users...</p>
        </div>
      </div>
    );
  }

  if (error && !allowedUsers.length) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchAllowedUsers}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2 className="admin-card-title">
          <FontAwesomeIcon icon={faUserPlus} className="icon" />
          Allowed Users
        </h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowModal(true)}
        >
          <FontAwesomeIcon icon={faPlus} /> Add User
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {/* Modal for adding a new allowed user */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                Add Allowed User
              </h3>
              <button className="close-button" onClick={resetForm}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddUser}>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter email address"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faUserTag} className="mr-2" />
                    Initial Role
                  </label>
                  <select
                    className="form-control"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    required
                  >
                    <option value="">-- Select Role --</option>
                    {roles
                      // Filter out Super Admin role for non-Super Admin users
                      .filter(role => isSuperAdmin || role.name !== 'Super Admin')
                      .map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))
                    }
                  </select>
                  <small className="form-text text-muted">
                    This role will be assigned when the user first logs in.
                    {selectedRole && roles.find(r => r.id === selectedRole)?.name === 'Admin' && (
                      <div className="text-info">
                        <span>Notes:Users with Admin role will automatically be assigned as account admins.</span>
                      </div>
                    )}
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faBuilding} className="mr-2" />
                    Initial Account
                  </label>
                  <select
                    className="form-control"
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    required
                  >
                    <option value="">-- Select Account --</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>{account.name}</option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    This account will be assigned when the user first logs in.
                  </small>
                </div>

                <div className="modal-footer">
                  <button type="submit" className="btn btn-success">Add User</button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {allowedUsers.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faUserPlus} className="empty-state-icon" />
          <p className="empty-state-text">No specifically allowed users defined yet</p>
          <p className="empty-state-subtext">
            Only users added to this list will be able to access the application.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            <FontAwesomeIcon icon={faPlus} /> Add First User
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('email')} className="sortable-header">
                  Email{getSortDirectionIndicator('email')}
                </th>
                <th onClick={() => requestSort('initial_role')} className="sortable-header">
                  Initial Role{getSortDirectionIndicator('initial_role')}
                </th>
                <th onClick={() => requestSort('initial_account')} className="sortable-header">
                  Initial Account{getSortDirectionIndicator('initial_account')}
                </th>
                <th onClick={() => requestSort('created_at')} className="sortable-header">
                  Added On{getSortDirectionIndicator('created_at')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAllowedUsers.map(user => (
                <tr key={user.id}>
                  <td>
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                    {user.email}
                  </td>
                  <td>
                    {user.initial_role_name ? (
                      <span className={`badge badge-${(user.initial_role_name === 'Admin' || user.initial_role_name === 'Super Admin') ? 'primary' : 'success'}`}>
                        {user.initial_role_name}
                      </span>
                    ) : (
                      <span className="text-muted">Default</span>
                    )}
                  </td>
                  <td>
                    {user.initial_account_name ? (
                      <span className="badge badge-info">{user.initial_account_name}</span>
                    ) : (
                      <span className="text-muted">None</span>
                    )}
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteUser(user.id)}
                        title="Remove User"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AllowedUserManagement;
